import React, { useEffect } from 'react';
import { MedicalDataTypes } from '../../../../../../primary-record-types';
import { getIntl } from '../../../../../../utils/intl';
import useMedicalProfileResource from '../../../../../../hooks/api/useMedicalProfileResource';
import { pluralResourceCategoryMessages, resourceCategoryMessages } from '../../../../../../utils/sharedMessages';
import * as Card from '../../../../../v2BaseComponents/Card';
import useRelatedResourceDiscoveries, {
    DiscoveryItem,
} from '../../../../../../hooks/api/useRelatedResourceDiscoveries';
import useMedicalProfileData, { BulkCreateArgs } from '../../../../../../hooks/api/useMedicalProfileData';
import Stack, { ContentStack } from '../../../../../v2BaseComponents/Stack';
import Text from '../../../../../v2BaseComponents/Text';
import { Checkbox } from '../../../../../v2BaseComponents/Inputs';
import { ActionButtonLayout } from '../../../../../v2BaseComponents/ActionButtonLayout';
import { AnimateLoadedContent } from '../../../../../v2BaseComponents/Loader';
import MainContentContainer from '../../../../../v2BaseComponents/MainContentContainer';
import { getSpacing } from '../../../../../../utils/spacing';
import Icon from '../../../../../v2BaseComponents/Icon';
import { PRScrollView } from '../../../../../shared/Scroll';

const intl = getIntl('discoveries-list', {
    suggestionHeader:
        'Primary Record found {relatedCategoryText} to {resourceDisplay}.  Select and link those that apply.',
    pluralRelatedCategoryText: 'some new {categoryText} that may relate',
    singularRelatedCategoryText: 'a new {categoryText} that maybe relates',
    noSuggestions: 'No {categoryText} found to add.',
    generatingSuggestions: 'Generating discoveries...',
    link: 'Link',
    nameFallback: 'this data',
    stillNotSeeing: 'Still not seeing what you are looking for?',
    manualText: 'Add Manually',
});

export type SuggestedAdditionsOwnProps = {
    sourceResourceCategory: MedicalDataTypes.PrimaryRecordResourceCategory;
    relatedResourceCategory: MedicalDataTypes.PrimaryRecordResourceCategory;
    hasEditPermission: boolean;
    relatedResources: MedicalDataTypes.PrimaryRecordResource[];
    isLoadingRelatedResources: boolean;
    profileId: string;
    sourceResourceId: string;
    onManualLink: () => void;
    onShowRelatedList: () => void;
};

export type SuggestedAdditionsProps = SuggestedAdditionsOwnProps;

const DiscoveryCard: React.FC<{ discovery: DiscoveryItem; onSelect: () => void; isSelected: boolean }> = ({
    discovery,
    onSelect,
    isSelected,
}) => (
    <Card.Card
        onPress={() => {
            onSelect();
        }}
    >
        <Card.CardLeftContent
            leftContent={
                <Stack vertical fullHeight padding={4} justify={'center'} align={'center'}>
                    <Checkbox value={!!isSelected} onChange={() => onSelect?.()} title="" />
                </Stack>
            }
        >
            <Card.CardHeader>{discovery.name}</Card.CardHeader>
            <Card.CardBody2>{discovery.explanation}</Card.CardBody2>
        </Card.CardLeftContent>
    </Card.Card>
);

const SuggestedAdditions: React.FC<SuggestedAdditionsProps> = ({
    relatedResourceCategory,
    relatedResources,
    profileId,
    sourceResourceId,
    sourceResourceCategory,
    onManualLink,
    onShowRelatedList,
}) => {
    const [selectedDiscoveryIds, setSelectedDiscoveryIds] = React.useState<string[]>([]);
    const resource = useMedicalProfileResource({
        medicalProfileId: profileId,
        resourceCategory: sourceResourceCategory,
        resourceId: sourceResourceId,
    });
    const discoveries = useRelatedResourceDiscoveries({
        profileId,
        resourceId: sourceResourceId,
        category: sourceResourceCategory,
        relatedCategory: relatedResourceCategory,
    });
    const isLoading = discoveries.isLoading || resource.isLoading;
    const hasResults = !!discoveries.discoveries?.length;
    const hasMultipleResults = (discoveries.discoveries?.length || 0) > 1;
    useEffect(() => {
        if (isLoading) {
            setSelectedDiscoveryIds([]);
        }
    }, [isLoading, relatedResources]);
    const { bulkCreateResources, isBulkCreatingResources } = useMedicalProfileData({
        medicalProfileId: profileId,
        onBulkCreateSuccess: onShowRelatedList,
    });
    const handleBulkCreateResources = () => {
        const bulkCreateArgs: BulkCreateArgs = {
            category: relatedResourceCategory,
            bulkCreateBody: {
                records: [],
            },
        };
        for (const selectedId of selectedDiscoveryIds) {
            const discovery = discoveries.discoveries?.find((d) => d.asResource.id === selectedId);
            if (discovery) {
                bulkCreateArgs.bulkCreateBody.records.push({
                    sourceResourceId: sourceResourceId,
                    resource: discovery.asResource,
                    images: [],
                });
            }
        }

        bulkCreateResources(bulkCreateArgs);
    };

    const getMessages = () => {
        if (isLoading) return null;

        const categoryText = hasMultipleResults
            ? pluralResourceCategoryMessages(relatedResourceCategory).toLocaleLowerCase()
            : resourceCategoryMessages(relatedResourceCategory).toLocaleLowerCase();
        const relatedCategoryText = intl(
            hasMultipleResults ? 'pluralRelatedCategoryText' : 'singularRelatedCategoryText',
            { categoryText },
        );
        const resourceDisplay = resource.resource?.name ? `"${resource.resource.name}"` : intl('nameFallback');
        return {
            header: intl('suggestionHeader', { relatedCategoryText, resourceDisplay }),
            noSuggestions: intl('noSuggestions', { categoryText }),
            categoryText,
        };
    };

    const getHeader = () => {
        if (isLoading) return null;

        const messages = getMessages();
        if (discoveries.discoveries?.length === 0) {
            <Stack horizontal justify={'center'}>
                {messages?.noSuggestions ? <Text variant="body-1">{messages?.noSuggestions}</Text> : null}
            </Stack>;
        } else {
            return (
                <Stack horizontal align={'center'} justify={'flex-start'} space={getSpacing(0.25)}>
                    <Icon name="AISparkle" size="button-icon" color="Primary-Reg" />
                    {messages?.header ? <Text variant="body-1">{messages?.header}</Text> : null}
                </Stack>
            );
        }
    };

    const renderResults = () => (
        <>
            {getHeader()}
            <PRScrollView style={{ flex: 1, width: '100%' }}>
                {discoveries.discoveries?.map((discovery) => (
                    <DiscoveryCard
                        key={discovery.asResource.id}
                        discovery={discovery}
                        onSelect={() => {
                            if (selectedDiscoveryIds.includes(discovery.asResource.id)) {
                                setSelectedDiscoveryIds(
                                    selectedDiscoveryIds.filter((id) => id !== discovery.asResource.id),
                                );
                            } else {
                                setSelectedDiscoveryIds([...selectedDiscoveryIds, discovery.asResource.id]);
                            }
                        }}
                        isSelected={selectedDiscoveryIds.includes(discovery.asResource.id)}
                    />
                ))}
            </PRScrollView>
        </>
    );

    return (
        <ActionButtonLayout
            scrollable
            actions={[
                {
                    title: intl('link'),
                    onPress: handleBulkCreateResources,
                    isDisabled: !selectedDiscoveryIds.length || isBulkCreatingResources || !hasResults || isLoading,
                    icon: 'Link',
                    testID: 'link-button',
                    show: !!selectedDiscoveryIds.length,
                },
                {
                    title: intl('manualText'),
                    onPress: onManualLink,
                    isDisabled: isBulkCreatingResources,
                    icon: 'PlusCircle',
                    testID: 'manual-add-link-button',
                },
            ]}
        >
            <AnimateLoadedContent isLoading={isLoading}>
                {() => (
                    <MainContentContainer>
                        <ContentStack>{renderResults()}</ContentStack>
                    </MainContentContainer>
                )}
            </AnimateLoadedContent>
        </ActionButtonLayout>
    );
};

export default React.memo(SuggestedAdditions);
